import { ChangeDetectionStrategy, Component, inject, NgZone, OnDestroy, OnInit, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser, DatePipe } from '@angular/common';
import { RouterLink } from "@angular/router";
import { ApiService } from '../../../core/services/api.service';
import { API_ROUTES } from '../../../core/constants/api-routes';
import { StorybookItem, StorybookListingData } from '../../../core/models/storybook.models';
import { DeleteConfirmation } from '../../../shared/components/delete-confirmation/delete-confirmation';

const PAGE_SIZE = 8;

@Component({
  selector: 'app-storybooks',
  imports: [RouterLink, DatePipe, DeleteConfirmation],
  templateUrl: './storybooks.html',
  styleUrl: './storybooks.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Storybooks implements OnInit, OnDestroy {

  private readonly api = inject(ApiService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly zone = inject(NgZone);

  // ── UI state ───────────────────────────────────────────────────────────────
  readonly isInitializing = signal(true);
  readonly hasError = signal(false);

  // ── Server data ────────────────────────────────────────────────────────────
  readonly storybooks = signal<StorybookItem[]>([]);
  readonly totalRecords = signal(0);
  readonly hasMoreStorybooks = signal(false);
  readonly isLoadingMore = signal(false);
  readonly hasStorybooksInSystem = signal(false);

  // ── Pagination state ───────────────────────────────────────────────────────
  readonly pageNumber = signal(1);

  // ── Delete modal state (mirrors loved-ones.ts pattern) ────────────────────
  /** Storybook pending removal confirmation — null when modal is closed. */
  readonly storybookToDelete = signal<StorybookItem | null>(null);

  private isRequestInFlight = false;
  private scrollObserver?: IntersectionObserver;

  // ── Lifecycle ──────────────────────────────────────────────────────────────
  ngOnInit(): void {
    this.loadStorybooks();
  }

  ngOnDestroy(): void {
    this.scrollObserver?.disconnect();
  }

  // ── API calls ──────────────────────────────────────────────────────────────
  async loadStorybooks(isLoadMore = false): Promise<void> {
    if (this.isRequestInFlight) return;
    if (isLoadMore && !this.hasMoreStorybooks()) return;

    this.isRequestInFlight = true;

    if (isLoadMore) {
      this.isLoadingMore.set(true);
    } else {
      this.hasError.set(false);
    }

    const params = new URLSearchParams();
    params.append('PageNumber', this.pageNumber().toString());
    params.append('PageSize', PAGE_SIZE.toString());

    const endpoint = `${API_ROUTES.STORY_BOOK_GET_LIST}?${params.toString()}`;

    try {
      const resp = await this.api.request<StorybookListingData>(
        'GET',
        endpoint,
        undefined,
        { showToaster: false, showLoader: !isLoadMore, useToken: true },
      );

      if (resp.status && resp.data) {
        const incoming = resp.data.storybooks ?? [];
        const total = resp.data.totalRecords ?? 0;

        if (isLoadMore) {
          this.storybooks.update(existing => [...existing, ...incoming]);
        } else {
          this.storybooks.set(incoming);
        }

        this.totalRecords.set(total);
        if (total > 0) this.hasStorybooksInSystem.set(true);
        this.hasMoreStorybooks.set(this.storybooks().length < total);
      } else {
        if (!isLoadMore) {
          this.storybooks.set([]);
          this.totalRecords.set(0);
          this.hasError.set(true);
        }
        this.hasMoreStorybooks.set(false);
      }
    } catch {
      if (!isLoadMore) {
        this.hasError.set(true);
        this.storybooks.set([]);
        this.totalRecords.set(0);
      }
      this.hasMoreStorybooks.set(false);
    } finally {
      this.isLoadingMore.set(false);
      this.isRequestInFlight = false;
      this.isInitializing.set(false);

      if (!isLoadMore && isPlatformBrowser(this.platformId)) {
        setTimeout(() => this.setupScrollObserver(), 100);
      }
    }
  }

  private setupScrollObserver(): void {
    this.scrollObserver?.disconnect();

    const sentinel = document.querySelector('#storybooksScrollSentinel');
    if (!sentinel) return;

    this.scrollObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !this.isRequestInFlight && this.hasMoreStorybooks()) {
          this.zone.run(() => {
            this.pageNumber.update(n => n + 1);
            this.loadStorybooks(true);
          });
        }
      },
      { threshold: 0.1 },
    );

    this.scrollObserver.observe(sentinel);
  }

  // ── Delete — mirrors loved-ones.ts confirmRemoveMember / cancelRemove / removeMember ──

  /** Opens the delete confirmation modal for a given storybook. */
  confirmDeleteStorybook(storybook: StorybookItem): void {
    this.storybookToDelete.set(storybook);
  }

  /** Closes the delete modal without performing any action. */
  cancelDelete(): void {
    this.storybookToDelete.set(null);
  }

  /**
   * Calls DELETE /Storybooks/DeleteStorybook/{storybookId}.
   * On success, removes the card from the list without a full reload.
   */
  async deleteStorybook(): Promise<void> {
    const storybook = this.storybookToDelete();
    if (!storybook) return;

    const resp = await this.api.request(
      'GET',
      `${API_ROUTES.STORY_BOOK_DELETE}/${storybook.storybookId}`,
      undefined,
      { showToaster: true, showLoader: true, useToken: true },
    );

    if (resp.status) {
      // Remove the deleted card from the local list — no full reload needed
      this.storybooks.update(list => list.filter(s => s.storybookId !== storybook.storybookId));
      this.totalRecords.update(n => Math.max(0, n - 1));
      this.storybookToDelete.set(null);
    }
  }
}
